"use client"

import * as React from 'react'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@open-mercato/ui/primitives/card'
import { Notice } from '@open-mercato/ui/primitives/Notice'
import { TabEmptyState } from '@open-mercato/ui/backend/detail'
import type { DriverDetail, DriverTranslate, ExpiryStatus } from './driversTable'

function formatDate(value: string | null): string {
  return value ?? '—'
}

function expiryVariant(status: ExpiryStatus): 'default' | 'destructive' | 'muted' | 'outline' {
  if (status === 'expired') return 'destructive'
  if (status === 'danger') return 'destructive'
  if (status === 'warning') return 'outline'
  if (status === 'missing') return 'muted'
  return 'default'
}

function expiryLabel(t: DriverTranslate, status: ExpiryStatus): string {
  switch (status) {
    case 'valid': return t('drivers.detail.status.valid', 'Valid')
    case 'warning': return t('drivers.detail.status.warning', 'Expiring soon')
    case 'danger': return t('drivers.detail.status.danger', 'Expiring very soon')
    case 'expired': return t('drivers.detail.status.expired', 'Expired')
    case 'missing': return t('drivers.detail.status.missing', 'Not provided')
  }
}

function SummaryField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1 rounded-md border bg-card px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value || '—'}</div>
    </div>
  )
}

export function DriversProfileTab({
  driver,
  t,
}: {
  driver: DriverDetail
  t: DriverTranslate
}) {
  return (
    <div id="drivers-section-profile" className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('drivers.detail.profile.personalTitle', 'Personal info')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <SummaryField label={t('drivers.detail.fields.firstName', 'First name')} value={driver.firstName} />
          <SummaryField label={t('drivers.detail.fields.lastName', 'Last name')} value={driver.lastName} />
          <SummaryField label={t('drivers.detail.fields.phone', 'Phone')} value={driver.phone} />
          <SummaryField label={t('drivers.detail.fields.email', 'Email')} value={driver.email} />
          <SummaryField label={t('drivers.detail.profile.peselLabel', 'PESEL')} value={driver.pesel} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('drivers.detail.profile.licenseTitle', 'Driving license')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <SummaryField
            label={t('drivers.detail.fields.licenseCategories', 'License categories')}
            value={driver.licenseCategories.length > 0 ? driver.licenseCategories.join(', ') : '—'}
          />
          <SummaryField label={t('drivers.detail.fields.licenseNumber', 'License number')} value={driver.licenseNumber} />
          <SummaryField label={t('drivers.detail.fields.licenseIssuedBy', 'Issued by')} value={driver.licenseIssuedBy} />
          <div className="sm:col-span-2 space-y-2">
            <SummaryField
              label={t('drivers.detail.fields.licenseExpires', 'Expires')}
              value={formatDate(driver.licenseExpires)}
            />
            <Badge variant={expiryVariant(driver.licenseExpiresStatus)}>
              {expiryLabel(t, driver.licenseExpiresStatus)}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function DriversQualificationsTab({
  driver,
  t,
}: {
  driver: DriverDetail
  t: DriverTranslate
}) {
  return (
    <div id="drivers-section-qualifications" className="grid gap-4 lg:grid-cols-3">
      <Card>
        <CardHeader>
          <CardTitle>{t('drivers.detail.qualifications.adrTitle', 'ADR Certificate')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SummaryField label={t('drivers.detail.qualifications.certificateNumber', 'Certificate number')} value={driver.adrCertificateNumber} />
          <SummaryField label={t('drivers.detail.qualifications.classes', 'Classes')} value={driver.adrCertificateClasses} />
          <SummaryField label={t('drivers.detail.qualifications.expiryDate', 'Expiry date')} value={formatDate(driver.adrExpires)} />
          {driver.adrExpiresDaysUntil !== null && (
            <SummaryField label={t('drivers.detail.qualifications.daysUntil', 'Days until expiry')} value={driver.adrExpiresDaysUntil} />
          )}
          <Badge variant={expiryVariant(driver.adrExpiresStatus)}>
            {expiryLabel(t, driver.adrExpiresStatus)}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('drivers.detail.qualifications.cpcTitle', 'CPC Certificate')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SummaryField label={t('drivers.detail.qualifications.certificateNumber', 'Certificate number')} value={driver.cpcNumber} />
          <SummaryField label={t('drivers.detail.qualifications.expiryDate', 'Expiry date')} value={formatDate(driver.cpcExpires)} />
          {driver.cpcExpiresDaysUntil !== null && (
            <SummaryField label={t('drivers.detail.qualifications.daysUntil', 'Days until expiry')} value={driver.cpcExpiresDaysUntil} />
          )}
          <Badge variant={expiryVariant(driver.cpcExpiresStatus)}>
            {expiryLabel(t, driver.cpcExpiresStatus)}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('drivers.detail.qualifications.tachographTitle', 'Digital tachograph')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SummaryField label={t('drivers.detail.qualifications.cardNumber', 'Card number')} value={driver.digitalTachographId} />
          <SummaryField label={t('drivers.detail.qualifications.expiryDate', 'Expiry date')} value={formatDate(driver.driverCardExpires)} />
          {driver.driverCardExpiresDaysUntil !== null && (
            <SummaryField label={t('drivers.detail.qualifications.daysUntil', 'Days until expiry')} value={driver.driverCardExpiresDaysUntil} />
          )}
          <Badge variant={expiryVariant(driver.driverCardExpiresStatus)}>
            {expiryLabel(t, driver.driverCardExpiresStatus)}
          </Badge>
        </CardContent>
      </Card>

      <div className="lg:col-span-3">
        <Card>
          <CardHeader>
            <CardTitle>{t('drivers.table.columns.qualifications', 'Qualifications')}</CardTitle>
          </CardHeader>
          <CardContent>
            {driver.qualifications.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {driver.qualifications.map((qual) => (
                  <Badge key={qual} variant="outline" className="bg-muted/30">
                    {qual}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function DriversPwaAccessTab({ driver, t }: { driver: DriverDetail; t: DriverTranslate }) {
  return (
    <div id="drivers-section-pwa-access">
      <Card>
        <CardHeader>
          <CardTitle>{t('drivers.detail.pwa.linkedUserTitle', 'Linked OM User')}</CardTitle>
        </CardHeader>
        <CardContent>
          {driver.userId ? (
            <SummaryField label={t('drivers.detail.pwa.userIdLabel', 'User ID')} value={driver.userId} />
          ) : (
            <Notice
              variant="info"
              message={t('drivers.detail.pwa.noAccess', 'No PWA access assigned')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export function DriversAssignmentHistoryTab({ t }: { t: DriverTranslate }) {
  return (
    <div id="drivers-section-assignment-history">
      <TabEmptyState
        title={t('drivers.detail.tabs.profile', 'Assignment history')}
        description="Dispatch assignment history will appear here once the road dispatch module is enabled."
      />
    </div>
  )
}
